import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  ResourceLayout,
  BottomDrawer,
  FormField,
  TextInput,
  Alert,
  Badge,
  Paper,
  Text,
  MobileListRow,
  type ResourceQuery,
} from '@vipso/shared';
import { Accordion, Checkbox, ScrollArea, Loader, Center } from '@mantine/core';
import {
  useListRolesQuery,
  useListFeatureGroupsQuery,
  useAddUpdateRoleMutation,
  useDeleteRoleMutation,
} from '../api/tenantApi';
import { useAccess } from '../hooks/useAccess';

type RoleRow = { id: string; name: string; featureKeys?: string[] };
type FeatureGroup = { module: string; label: string; keys: string[] };

function actionLabel(key: string): string {
  const segment = key.split('.').at(-1) ?? key;
  return segment.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function ModulePermissions({
  mod,
  selected,
  onChange,
}: {
  mod: FeatureGroup;
  selected: string[];
  onChange: (keys: string[]) => void;
}) {
  const allSelected = mod.keys.every((k) => selected.includes(k));
  const someSelected = mod.keys.some((k) => selected.includes(k));

  function toggleAll() {
    if (allSelected) {
      onChange(selected.filter((k) => !mod.keys.includes(k)));
    } else {
      const added = mod.keys.filter((k) => !selected.includes(k));
      onChange([...selected, ...added]);
    }
  }

  function toggleOne(key: string) {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  }

  return (
    <Stack gap={6}>
      <Checkbox
        label={<Text fw={600} size="sm">{mod.label}</Text>}
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={toggleAll}
      />
      <Stack gap={4} pl="md">
        {mod.keys.map((key) => (
          <Checkbox
            key={key}
            label={<Text size="sm" c="dimmed">{actionLabel(key)}</Text>}
            checked={selected.includes(key)}
            onChange={() => toggleOne(key)}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export function RolesPage() {
  const { t } = useTranslation();
  const { canMutate } = useAccess();
  const [query, setQuery] = useState<ResourceQuery>({ page: 1, pageLimit: 20, search: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [form, setForm] = useState({ name: '', featureKeys: [] as string[] });

  const { data, isLoading, isFetching, isError } = useListRolesQuery();
  const { data: groupsData, isLoading: groupsLoading } = useListFeatureGroupsQuery();
  const [save, { isLoading: saving }] = useAddUpdateRoleMutation();
  const [remove] = useDeleteRoleMutation();

  const permissionModules = useMemo(
    () => groupsData?.listFeatureGroups ?? [],
    [groupsData?.listFeatureGroups],
  );

  const roles = (data?.listRoles ?? []) as RoleRow[];
  const search = query.search?.toLowerCase() ?? '';
  const filtered = search ? roles.filter((r) => r.name.toLowerCase().includes(search)) : roles;
  const paged = filtered.slice(
    ((query.page ?? 1) - 1) * (query.pageLimit ?? 20),
    (query.page ?? 1) * (query.pageLimit ?? 20),
  );
  const list = { rows: paged, total: filtered.length, page: query.page ?? 1, pageLimit: query.pageLimit ?? 20 };

  function openCreate() {
    setEditing(null);
    setForm({ name: '', featureKeys: [] });
    setModalOpen(true);
  }

  function openEdit(row: RoleRow) {
    setEditing(row);
    setForm({ name: row.name, featureKeys: row.featureKeys ?? [] });
    setModalOpen(true);
  }

  async function onSave() {
    await save({
      ...(editing ? { id: editing.id } : {}),
      name: form.name.trim(),
      featureKeys: form.featureKeys,
    }).unwrap();
    setModalOpen(false);
  }

  return (
    <Stack gap="md">
      <PageHeader title={t('roles.title')} description={t('roles.description')} />

      {isError ? <Alert color="red" title={t('roles.loadFailed')} /> : null}

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={t('roles.roleName')}
        emptyTitle={t('roles.noRoles')}
        emptyDescription={t('roles.noRoles')}
        onRowClick={openEdit}
        mobileView="list"
        addButton={
          canMutate() ? (
            <Button onClick={openCreate}>{t('roles.addRole')}</Button>
          ) : undefined
        }
        columns={[
          {
            key: 'name',
            header: t('roles.roleName'),
            render: (row: RoleRow) => row.name,
          },
          {
            key: 'permissions',
            header: t('roles.permissions'),
            render: (row: RoleRow) => {
              const keys = row.featureKeys ?? [];
              if (!keys.length) return <Text c="dimmed" size="sm">—</Text>;
              return (
                <Group gap={4} wrap="wrap">
                  {keys.slice(0, 3).map((k) => (
                    <Badge key={k} size="xs" variant="light">{k}</Badge>
                  ))}
                  {keys.length > 3 ? (
                    <Badge size="xs" color="gray" variant="light">
                      +{keys.length - 3}
                    </Badge>
                  ) : null}
                </Group>
              );
            },
          },
        ]}
        renderCard={(row: RoleRow) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Text fw={600}>{row.name}</Text>
              <Group gap={4} wrap="wrap">
                {(row.featureKeys ?? []).slice(0, 4).map((k) => (
                  <Badge key={k} size="xs" variant="light">{k}</Badge>
                ))}
                {(row.featureKeys ?? []).length > 4 ? (
                  <Badge size="xs" color="gray" variant="light">
                    +{(row.featureKeys ?? []).length - 4}
                  </Badge>
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: RoleRow) => (
          <MobileListRow
            title={row.name}
            subtitle={`${(row.featureKeys ?? []).length} ${t('roles.permissions').toLowerCase()}`}
            onClick={() => openEdit(row)}
          />
        )}
      />

      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? t('roles.editRole') : t('roles.addRole')}
      >
        <Stack gap="md">
          <FormField label={t('roles.roleName')} required>
            <TextInput
              value={form.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.currentTarget.value })}
              placeholder={t('roles.roleName')}
            />
          </FormField>

          <FormField label={t('roles.permissions')}>
            {groupsLoading ? (
              <Center py="xl">
                <Loader size="sm" />
              </Center>
            ) : (
              <ScrollArea h={400} type="auto">
                <Accordion multiple variant="separated" chevronPosition="right">
                  {permissionModules.map((mod) => {
                    const selectedCount = mod.keys.filter((k) =>
                      form.featureKeys.includes(k),
                    ).length;
                    return (
                      <Accordion.Item key={mod.module} value={mod.module}>
                        <Accordion.Control>
                          <Group justify="space-between" pr="sm">
                            <Text size="sm" fw={500}>{mod.label}</Text>
                            {selectedCount > 0 ? (
                              <Badge size="xs" variant="filled" circle>
                                {selectedCount}
                              </Badge>
                            ) : null}
                          </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                          <ModulePermissions
                            mod={mod}
                            selected={form.featureKeys}
                            onChange={(keys) => setForm({ ...form, featureKeys: keys })}
                          />
                        </Accordion.Panel>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </ScrollArea>
            )}
          </FormField>

          <Group justify="space-between">
            {editing ? (
              <Button
                color="red"
                variant="light"
                onClick={async () => {
                  await remove(editing.id);
                  setModalOpen(false);
                }}
              >
                {t('roles.deleteRole')}
              </Button>
            ) : (
              <span />
            )}
            <Button
              loading={saving}
              disabled={!form.name.trim()}
              onClick={() => void onSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
